// Event is of type IpcRendererEvent, which doesn't exist in the web app right now, so just use any
import { MessageThread } from './messageThread'
import { PhoneCall } from './phone'
import { ObjectValues } from './utility'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IpcRendererListener = (event: any, ...args: any[]) => void

type PropagateBrowserChangeFn = <T>(name: string, value: T | null) => void
type RegisterVariableObserverBridgeFn = <T>(
  name: string,
  onChange: (value: T | null) => void,
) => () => void
type GetCurrentValueFn = <T>(name: string) => Promise<T | null>

type VariableObserver = {
  registerVariableObserverBridge: RegisterVariableObserverBridgeFn
  propagateBrowserChange: PropagateBrowserChangeFn
  getCurrentValue: GetCurrentValueFn
}

interface SharedVariableBridge {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue(variableName: string, value: any): void
  subscribe(
    variableName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (value: any) => void,
  ): () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue(variableName: string): any
}

interface PopupWindowBridge {
  sendExperimentSetting: (value: string) => void
}

interface ClipboardBridge {
  writeText: (text: string) => void
}

interface UpdatesBridge {
  check: () => void
  quitAndInstall: () => void
}

interface YealinkDeviceBridge {
  callMuteChanged: (mute: boolean, cid: number) => void
  callHoldChanged: (
    hold: boolean,
    cid: number,
    caller: { name: string; number: string },
  ) => void
  callStarted: (cid: number) => void
  callTerminated: (cid: number) => void
  callRinging: (ringing: boolean, cid: number) => void
  incomingCall: (cid: number, caller: { name: string; number: string }) => void
  outboundCall: (cid: number, caller: { name: string; number: string }) => void
  reset: () => void
}

export interface ElectronBridge {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  send: (channel: string, ...args: any[]) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  invoke: (channel: string, ...args: any[]) => Promise<any>
  on: (channel: string, func: IpcRendererListener) => void
  once: (channel: string, func: IpcRendererListener) => void
  removeAllListeners: (channel: string) => void
  removeListener: (channel: string, func: IpcRendererListener) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  receive: (channel: string, func: (...args: any[]) => void) => void
  yealinkDevice: YealinkDeviceBridge
  popupWindow: PopupWindowBridge
  sharedVariables: SharedVariableBridge
  variableObserver: VariableObserver
  clipboard: ClipboardBridge
  updates: UpdatesBridge
}

export const PopupQueueItemTypes = {
  CALL: 'call',
  MESSAGE: 'message',
} as const

export type PopupQueueItemType = ObjectValues<typeof PopupQueueItemTypes>

export type PopupQueueItemBase = {
  _id: string
  type: PopupQueueItemType
}

export type CallQueueItem = Partial<PhoneCall> & PopupQueueItemBase
export type MessageQueueItem = Partial<MessageThread> & PopupQueueItemBase

export type PopupQueueItem = CallQueueItem | MessageQueueItem

export const SharedVariables = {
  CallQueue: 'CallQueue',
  MessageQueue: 'MessageQueue',
  ShowCallModal: 'ShowCallModal',
  ParkingLots: 'ParkingLots',
  CallMetadata: 'CallMetadata',
} as const

export type CallMetadata = {
  hasNoActiveCall: boolean
  mustRejectOrEndCall: boolean
  canParkOrHoldCall: boolean
}
